import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SendMessage } from "./SendMessage";
import { useAudioHandler } from "./useAudioHandler";
import { useProgressTracker } from "./useProgressTracker";
import { MessageRenderer } from "./MessageRenderer";
import "./styles.scss";
import { useAuth } from "@/contexts/auth-context";
import { Button, Icon, ProgressBar } from "@/components";

export interface Option {
  id: string;
  text: string;
  audio?: string;
  audioUrl?: string;
}

export interface QuestionContent {
  audioUrl?: string;
  text?: string;
  imageUrl?: string;
  videoUrl?: string;
}

export interface SeriesItem {
  id: number;
  title: string;
  answer: string[];
  question: QuestionContent;
  options: Option[];
}

export interface Question {
  series: SeriesItem[];
}

export type Message =
  | {
      type: "question";
      content: QuestionContent | string;
      id: number;
    }
  | {
      type: "answer";
      content: Option[];
      correct: boolean;
      id: number;
    };

export const VoiceChatActivity: React.FC<{ question: Question }> = ({
  question,
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [answered, setAnswered] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showFullInput, updateShowFullInput] = useState<boolean>(true);
  const listEndRef = useRef<HTMLDivElement>(null);
  const { level_id, lesson_id } = useParams();
  const { updateTrialCount, updateGameTimeStamp } = useAuth();
  const navigate = useNavigate();

  const {
    audioStates,
    setAudioStates,
    waveformData,
    loadAudio,
    toggleAudio,
    cleanupAudio,
    waveformBars,
  } = useAudioHandler();

  const progress = useProgressTracker(question, messages);
  const currentSeries = question.series[currentQuestionIndex];

  const scrollToBottom = () => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    setMessages((prev) => {
      if (
        !prev.some(
          (msg) => msg.id === currentQuestionIndex && msg.type === "question"
        )
      ) {
        return [
          ...prev,
          {
            type: "question",
            content: currentSeries.question,
            id: currentQuestionIndex,
          },
        ];
      }
      return prev;
    });

    return cleanupAudio;
  }, [currentQuestionIndex]);

  useEffect(() => {
    scrollToBottom();
  }, [
    messages.length,
    selectedOptions,
    currentQuestionIndex,
    currentSeries.id,
  ]);

  const handleOptionSelect = (option: Option) => {
    setSelectedOptions((prev) => {
      if (prev.find((item) => item.id === option.id)) {
        return prev.filter((item) => item.id !== option.id);
      } else {
        return [...prev, option];
      }
    });
  };

  const handleSend = () => {
    if (selectedOptions.length === 0 || answered) return;
    setAnswered(true);

    const selectedIds = selectedOptions.map((opt) => opt.id);
    const correctIds = currentSeries.answer;

    const isCorrect =
      selectedIds.length === correctIds.length &&
      selectedIds.every((id, index) => id === correctIds[index]);

    setMessages((prev) => [
      ...prev,
      {
        type: "answer",
        content: selectedOptions,
        correct: isCorrect,
        id: currentQuestionIndex,
      },
    ]);

    if (isCorrect) {
      if (currentQuestionIndex + 1 < question.series.length) {
        updateShowFullInput(true);
        scrollToBottom();
        setTimeout(() => {
          setSelectedOptions([]);
          setAnswered(false);
          setCurrentQuestionIndex((prev) => prev + 1);
        }, 1000);
      } else {
        updateGameTimeStamp((prev: any) => ({ ...prev, end: Date.now() }));
        setTimeout(
          () =>
            navigate(
              `/activity/level/${level_id}/lesson/${lesson_id}/brief/trivia-activity/complete`
            ),
          800
        );
      }
    }
  };

  const handleRetry = () => {
    updateTrialCount((prev: number) => prev + 1);
    setMessages((prev) =>
      prev.filter(
        (msg) => msg.type !== "answer" || msg.id !== currentQuestionIndex
      )
    );
    setSelectedOptions([]);
    setAnswered(false);
  };

  return (
    <div className="voicechat-container">
      <div className="top-bar">
        <Button variant="neutral" onClick={() => navigate(-2)}>
          <Icon name="close" />
        </Button>
        <ProgressBar percent={Number(progress)} />
      </div>

      <div className="middle-container">
        <div className="message-list">
          {messages.map((item, index) => (
            <MessageRenderer
              key={`${item.type}-${item.id}-${index}`}
              item={item}
              audioStates={audioStates}
              setAudioStates={setAudioStates}
              waveformData={waveformData}
              loadAudio={loadAudio}
              toggleAudio={toggleAudio}
              handleRetry={handleRetry}
              waveformBars={waveformBars}
            />
          ))}
          <div ref={listEndRef} />
        </div>

        <div className="options-wrapper">
          {!answered && !showFullInput && (
            <>
              <div className="selected-options">
                {selectedOptions.map((item, index) => (
                  <span className="each-selected-option" key={index}>
                    {item.text}
                  </span>
                ))}
              </div>
              <div className="options">
                {currentSeries?.options.map((item, index) => (
                  <button
                    className={`option ${
                      selectedOptions.find((opt) => opt.id === item.id)
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleOptionSelect(item)}
                    disabled={answered}
                    key={index}
                  >
                    {item.text}
                  </button>
                ))}
              </div>
            </>
          )}
          <SendMessage
            updateShowFullInput={updateShowFullInput}
            showFullInput={showFullInput}
            selectedOptions={selectedOptions}
            handleSend={handleSend}
            answered={answered}
          />
        </div>
      </div>
    </div>
  );
};
